import React, { useState, useEffect } from 'react';
import {
    IoCloudUploadOutline, IoCheckmarkCircle, IoDocumentTextOutline,
    IoEyeOutline, IoClose, IoRefreshOutline, IoSyncOutline,
    IoAlertCircleOutline, IoCheckboxOutline, IoSquareOutline,
    IoWalletOutline, IoCardOutline, IoBusinessOutline, IoSaveOutline,
    IoChevronDown
} from 'react-icons/io5';
import './onboarding.css';

const API_URL = process.env.REACT_APP_API_URL;

// Toast Component
const Toast = ({ show, type, message, onClose }) => {
    if (!show) return null;
    return (
        <div className={`toast-notification show ${type}`}>
            <div className="toast-content">
                <div className="toast-title">{type === 'success' ? 'Success' : 'Error'}</div>
                <div className="toast-msg">{message}</div>
            </div>
            <IoClose className="toast-close-btn" onClick={onClose} />
        </div>
    );
};

// Document Card Component
const DynamicDocCard = ({ doc, onUpload, isUploading }) => {
    const isUploaded = doc.status === 'uploaded' && doc.uploaded_doc_id;

    return (
        <div className={`doc-card ${isUploaded ? 'uploaded' : ''} ${isUploading ? 'processing' : ''}`}>
            <div className="doc-card-header">
                <div className={`doc-icon-box ${isUploaded ? 'success' : 'neutral'}`}>
                    {isUploading ? <IoSyncOutline className="spin-icon" size={22} /> :
                        isUploaded ? <IoCheckmarkCircle size={22} /> : <IoDocumentTextOutline size={22} />}
                </div>
                <div className={`status-badge ${isUploaded ? 'done' : (isUploading ? 'progress' : 'pending')}`}>
                    {isUploading ? 'Uploading...' : (isUploaded ? 'Completed' : doc.is_mandatory ? 'Required' : 'Optional')}
                </div>
            </div>
            <div className="doc-content">
                <h3 className="doc-title">
                    {doc.document_name}
                    {doc.is_mandatory && <span style={{ color: '#EF4444', marginLeft: '4px' }}>*</span>}
                </h3>
                <p className="doc-desc">{doc.description || 'Please upload this document'}</p>
                {isUploaded && doc.file_name && (
                    <div className="uploaded-filename" title={doc.file_name}>
                        <IoCheckmarkCircle size={12} /> {doc.file_name}
                    </div>
                )}
            </div>
            <div className="doc-actions">
                {isUploading ? (
                    <button className="btn-action btn-disabled" disabled>Processing...</button>
                ) : isUploaded ? (
                    <div className="uploaded-actions-row">
                        <button className="btn-action btn-view" onClick={() => window.open(doc.file_url, '_blank')}>
                            <IoEyeOutline size={16} /> View
                        </button>
                        <label className="btn-action btn-reupload">
                            <IoRefreshOutline size={16} /> Update
                            <input
                                type="file"
                                accept=".pdf,.jpg,.png,.jpeg"
                                onClick={(e) => e.target.value = null}
                                onChange={(e) => onUpload(doc.document_key, e.target.files[0])}
                                hidden
                            />
                        </label>
                    </div>
                ) : (
                    <label className="btn-action btn-upload">
                        <IoCloudUploadOutline size={18} /> Select File
                        <input
                            type="file"
                            accept=".pdf,.jpg,.png,.jpeg"
                            onClick={(e) => e.target.value = null}
                            onChange={(e) => onUpload(doc.document_key, e.target.files[0])}
                        />
                    </label>
                )}
            </div>
        </div>
    );
};

// Main Component
const DynamicOnboardingEmployee = ({ user }) => {
    const [requiredDocs, setRequiredDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });

    // Bank Details State
    const [bankDetails, setBankDetails] = useState({
        account_number: '',
        ifsc_code: '',
        bank_name: '',
        branch_name: '',
        contact_number: ''
    });
    const [bankNames, setBankNames] = useState([]);
    const [savingBank, setSavingBank] = useState(false);
    const [bankSaved, setBankSaved] = useState(false);

    const getAuthHeaders = () => ({
        'Authorization': `Bearer ${localStorage.getItem('zTaskAuthToken') || ''}`
    });

    useEffect(() => {
        if (user && user.user_ID) {
            fetchRequiredDocuments();
            fetchBankNames();
            fetchBankDetails();
        }
    }, [user]);

    const fetchRequiredDocuments = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/hr-documents/user/${user.user_ID}/required-documents`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();

            if (res.ok) {
                setRequiredDocs(data.documents || []);
            } else {
                showToast(data.error || 'Failed to fetch documents', 'error');
            }
        } catch (error) {
            console.error("Error fetching required docs", error);
            showToast('Network error. Please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpload = async (documentKey, file) => {
        if (!file) return;

        const fileSizeMB = file.size / 1024 / 1024;
        if (fileSizeMB > 10) {
            showToast("File size too large. Please upload files under 10MB.", "error");
            return;
        }

        setUploadingDoc(documentKey);

        const formData = new FormData();
        formData.append('user_id', user.user_ID);
        formData.append(documentKey, file);

        try {
            const res = await fetch(`${API_URL}/documents/upload`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: formData
            });

            const data = await res.json();

            if (res.ok) {
                showToast(`${file.name} uploaded successfully! HR has been notified.`, 'success');
                fetchRequiredDocuments(); // Refresh list
            } else {
                showToast(data.error || 'Upload failed', 'error');
            }
        } catch (error) {
            showToast('Network error during upload', 'error');
        } finally {
            setUploadingDoc(null);
        }
    };

    const showToast = (message, type) => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: '' }), 5000);
    };

    // Fetch Bank Names from Master
    const fetchBankNames = async () => {
        try {
            // Construct the correct API URL
            const baseUrl = API_URL.includes('/api') ? API_URL.replace('/api', '') : API_URL;
            const url = `${baseUrl}/api/get-column/common_master/bank_name`;

            const token = localStorage.getItem('zTaskAuthToken');
            console.log('Fetching bank names from:', url);
            console.log('Using Auth Token from Storage:', !!token);

            const res = await fetch(url, {
                // GET requests typically don't need Content-Type, and it can trigger preflight
                headers: {
                    'Authorization': `Bearer ${token || ''}`
                }
            });

            if (!res.ok) {
                console.error('Failed to fetch bank names, status:', res.status);

                if (res.status === 401) {
                    showToast('Authentication failed. Please check your login session.', 'error');
                } else if (res.status === 404) {
                    showToast('Bank details not found on server.', 'error');
                } else {
                    showToast(`Failed to load bank list (Status: ${res.status})`, 'error');
                }
                return;
            }

            const data = await res.json();
            console.log('Bank names received:', data);

            if (Array.isArray(data) && data.length > 0) {
                const formattedBanks = data.map(b => ({ value: b.value, id: b.id }));
                setBankNames(formattedBanks);
            } else {
                console.warn('No bank names found in response');
                showToast('No bank names found in database.', 'warning');
            }
        } catch (error) {
            console.error("Error fetching bank names:", error);
            showToast('Network error while fetching bank names.', 'error');
        }
    };

    // Fetch existing bank details
    const fetchBankDetails = async () => {
        try {
            const res = await fetch(`${API_URL}/hr/employees/${user.user_ID}`, {
                headers: getAuthHeaders()
            });
            const data = await res.json();
            if (res.ok && (data.acno || data.ifsc_code)) {
                setBankDetails({
                    account_number: data.acno || '',
                    ifsc_code: data.ifsc_code || '',
                    bank_name: data.bankname_id || '',
                    branch_name: data.branch || '',
                    contact_number: data.contact_details || ''
                });
                setBankSaved(true);
            }
        } catch (error) {
            console.error("Error fetching bank details", error);
        }
    };

    // Save Bank Details
    const handleSaveBankDetails = async () => {
        // Validation
        if (!bankDetails.account_number || !bankDetails.ifsc_code || !bankDetails.bank_name || !bankDetails.branch_name || !bankDetails.contact_number) {
            showToast('Please fill all bank details fields', 'error');
            return;
        }

        // IFSC validation (basic)
        if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifsc_code.toUpperCase())) {
            showToast('Invalid IFSC code format', 'error');
            return;
        }

        setSavingBank(true);
        try {
            // Get emp_code from user data
            const userRes = await fetch(`${API_URL}/hr/employees/${user.user_ID}`, {
                headers: getAuthHeaders()
            });
            const userData = await userRes.json();

            const res = await fetch(`${API_URL}/hr/employees/bank-details`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                body: JSON.stringify({
                    emp_code: userData.emp_code,
                    account_number: bankDetails.account_number,
                    ifsc_code: bankDetails.ifsc_code.toUpperCase(),
                    bank_name: bankNames.find(b => b.id == bankDetails.bank_name)?.value || bankDetails.bank_name,
                    branch_name: bankDetails.branch_name,
                    contact_number: bankDetails.contact_number
                })
            });

            const data = await res.json();

            if (res.ok) {
                showToast('Bank details saved successfully!', 'success');
                setBankSaved(true);
            } else {
                showToast(data.error || 'Failed to save bank details', 'error');
            }
        } catch (error) {
            console.error("Error saving bank details", error);
            showToast('Network error. Please try again.', 'error');
        } finally {
            setSavingBank(false);
        }
    };

    // Calculate progress
    const uploadedCount = requiredDocs.filter(doc => doc.status === 'uploaded').length;
    const mandatoryCount = requiredDocs.filter(doc => doc.is_mandatory).length;
    const mandatoryUploaded = requiredDocs.filter(doc => doc.is_mandatory && doc.status === 'uploaded').length;
    const progress = requiredDocs.length > 0 ? Math.round((uploadedCount / requiredDocs.length) * 100) : 0;
    const isComplete = mandatoryCount > 0 && mandatoryUploaded === mandatoryCount;

    if (loading) {
        return (
            <div className="portal-loading-screen">
                <div className="loader"></div>
                Loading Your Documents...
            </div>
        );
    }

    if (requiredDocs.length === 0) {
        return (
            <div className="employee-portal-wrapper">
                <Toast show={toast.show} type={toast.type} message={toast.message} onClose={() => setToast({ show: false })} />

                <div className="portal-header-banner">
                    <div className="header-text-content">
                        <h1>Welcome, {user.name ? user.name.split(' ')[0] : 'Employee'}!</h1>
                        <p style={{ opacity: 0.9, marginTop: '12px', fontSize: '15px' }}>
                            <IoAlertCircleOutline size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                            No documents have been assigned to you yet. Please contact HR to proceed with your onboarding.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Group documents by mandatory/optional
    const mandatoryDocs = requiredDocs.filter(doc => doc.is_mandatory);
    const optionalDocs = requiredDocs.filter(doc => !doc.is_mandatory);

    return (
        <div className="employee-portal-wrapper">
            <Toast show={toast.show} type={toast.type} message={toast.message} onClose={() => setToast({ show: false })} />

            <div className="portal-header-banner">
                <div className="header-text-content">
                    <h1>Hello, {user.name ? user.name.split(' ')[0] : 'Employee'}</h1>
                    <p style={{ opacity: 0.85, marginTop: '8px' }}>
                        {isComplete
                            ? '🎉 All mandatory documents uploaded! Your onboarding is complete.'
                            : `Please upload your documents to complete your onboarding. ${mandatoryUploaded}/${mandatoryCount} mandatory documents uploaded.`}
                    </p>
                </div>
                <div className="progress-card">
                    <div className="progress-label">Overall Progress</div>
                    <div className="progress-percent">{progress}%</div>
                    <div className="progress-bar-bg">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                    <div style={{ fontSize: '10px', marginTop: '6px', opacity: 0.8 }}>
                        {uploadedCount} of {requiredDocs.length} documents
                    </div>
                </div>
            </div>

            {/* Bank Details Section (Restricted to HR) */}
            {false && <div className="portal-content-area" style={{ marginBottom: '30px' }}>
                <div className="section-container">
                    <div className="section-label" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <IoWalletOutline size={18} /> Bank Account Details
                        {bankSaved && <span style={{ marginLeft: 'auto', fontSize: '12px', color: '#10B981', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <IoCheckmarkCircle size={14} /> Saved
                        </span>}
                    </div>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                                    Account Number <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter account number"
                                    value={bankDetails.account_number}
                                    onChange={(e) => setBankDetails({ ...bankDetails, account_number: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        transition: 'border-color 0.2s'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                                    IFSC Code <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="e.g. SBIN0001234"
                                    value={bankDetails.ifsc_code}
                                    onChange={(e) => setBankDetails({ ...bankDetails, ifsc_code: e.target.value.toUpperCase() })}
                                    maxLength={11}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        textTransform: 'uppercase'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                                    Bank Name <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <select
                                    value={bankDetails.bank_name}
                                    onChange={(e) => setBankDetails({ ...bankDetails, bank_name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none',
                                        backgroundColor: 'white',
                                        cursor: 'pointer'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                >
                                    <option value="">Select Bank</option>
                                    {bankNames.map(bank => (
                                        <option key={bank.id} value={bank.id}>{bank.value}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                                    Branch Name <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Enter branch name"
                                    value={bankDetails.branch_name}
                                    onChange={(e) => setBankDetails({ ...bankDetails, branch_name: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '8px' }}>
                                    Contact Number <span style={{ color: '#EF4444' }}>*</span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="Enter contact number"
                                    value={bankDetails.contact_number}
                                    onChange={(e) => setBankDetails({ ...bankDetails, contact_number: e.target.value })}
                                    maxLength={10}
                                    style={{
                                        width: '100%',
                                        padding: '10px 14px',
                                        border: '1px solid #E2E8F0',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        outline: 'none'
                                    }}
                                    onFocus={(e) => e.target.style.borderColor = '#4F46E5'}
                                    onBlur={(e) => e.target.style.borderColor = '#E2E8F0'}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                onClick={handleSaveBankDetails}
                                disabled={savingBank}
                                style={{
                                    padding: '10px 24px',
                                    background: savingBank ? '#94A3B8' : '#4F46E5',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    cursor: savingBank ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background 0.2s'
                                }}
                                onMouseEnter={(e) => !savingBank && (e.target.style.background = '#4338CA')}
                                onMouseLeave={(e) => !savingBank && (e.target.style.background = '#4F46E5')}
                            >
                                {savingBank ? (
                                    <><IoSyncOutline className="spin-icon" size={16} /> Saving...</>
                                ) : (
                                    <><IoSaveOutline size={16} /> {bankSaved ? 'Update' : 'Save'} Bank Details</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>}

            <div className="portal-content-area">
                {mandatoryDocs.length > 0 && (
                    <div className="section-container">
                        <div className="section-label">
                            <span style={{ color: '#EF4444' }}>●</span> Required Documents
                        </div>
                        <div className="doc-grid">
                            {mandatoryDocs.map(doc => (
                                <DynamicDocCard
                                    key={doc.requirement_id}
                                    doc={doc}
                                    onUpload={handleUpload}
                                    isUploading={uploadingDoc === doc.document_key}
                                />
                            ))}
                        </div>
                    </div>
                )}

                {optionalDocs.length > 0 && (
                    <div className="section-container">
                        <div className="section-label">
                            <span style={{ color: '#94A3B8' }}>○</span> Optional Documents
                        </div>
                        <div className="doc-grid">
                            {optionalDocs.map(doc => (
                                <DynamicDocCard
                                    key={doc.requirement_id}
                                    doc={doc}
                                    onUpload={handleUpload}
                                    isUploading={uploadingDoc === doc.document_key}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DynamicOnboardingEmployee;
